import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarClock, Radio, CalendarPlus } from "lucide-react";
import { useApi } from "@/lib/useApi";
import { useLang } from "@/context/AppContext";
import { useLiveEvent } from "@/context/RealtimeContext";
import { PageHead } from "@/pages/app/Dashboard";

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const sameDay = (a, b) => dayKey(a) === dayKey(b);
const TONE = { interview: "bg-yellow-400", broadcast: "bg-rose-400" };

const EventRow = ({ e }) => {
  const { t } = useLang();
  const Icon = e.kind === "interview" ? CalendarClock : Radio;
  const status = e.kind === "broadcast" ? t(e.status === "live" ? "live_status" : "ended_status") : t(`st_${e.state}`);
  return (
    <Link to={e.link} className="flex items-start gap-3 rounded-md border border-white/5 p-3 transition-colors hover:border-emerald-400/30" data-testid={`agenda-event-${e.kind}-${e.id}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${e.kind === "interview" ? "text-yellow-300" : "text-rose-300"}`} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-white">{e.title}</p>
        <p className="font-mono text-[10px] text-slate-500">
          {new Date(e.start).toLocaleString([], { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })} · {t(e.kind === "interview" ? "interview_kind" : "broadcast_kind")} · {status}
        </p>
        <p className="truncate text-xs text-slate-400">{e.with}{e.community && ` · ${e.community}`}</p>
      </div>
    </Link>
  );
};

const groupByDay = (items) => items.reduce((m, e) => { const k = dayKey(new Date(e.start)); (m[k] ||= []).push(e); return m; }, {});
const monthCells = (month) => {
  const offset = (month.getDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, i) => new Date(month.getFullYear(), month.getMonth(), i - offset + 1));
};
const isUpcoming = (e, now) => (e.kind === "interview" ? new Date(e.end).getTime() >= now : e.status === "live");
const firstOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);

const DayCell = ({ d, evs, inMonth, isSel, onSelect }) => {
  const isToday = sameDay(d, new Date());
  return (
    <button onClick={() => onSelect(d)} data-testid={`agenda-day-${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`}
      className={`flex min-h-[72px] flex-col items-start gap-1 bg-[#0B0F17] p-2 text-left transition-colors hover:bg-[#151d2e] ${isSel ? "ring-1 ring-inset ring-emerald-400" : ""} ${inMonth ? "" : "opacity-35"}`}>
      <span className={`grid h-6 w-6 place-items-center rounded-full font-mono text-xs ${isToday ? "bg-emerald-500 text-slate-950" : "text-slate-300"}`}>{d.getDate()}</span>
      <div className="flex flex-wrap gap-1">
        {evs.slice(0, 4).map((e) => <span key={e.kind + e.id} className={`h-1.5 w-1.5 rounded-full ${TONE[e.kind]}`} />)}
        {evs.length > 4 && <span className="font-mono text-[9px] text-slate-500">+{evs.length - 4}</span>}
      </div>
    </button>
  );
};

const MonthNav = ({ month, lang, onShift, onToday }) => {
  const { t } = useLang();
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="font-display text-xl font-bold capitalize text-white" data-testid="agenda-month-label">{month.toLocaleDateString(lang, { month: "long", year: "numeric" })}</h2>
      <div className="flex items-center gap-2">
        <button onClick={() => onShift(-1)} className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-slate-300 hover:text-white" data-testid="agenda-prev-month"><ChevronLeft className="h-4 w-4" /></button>
        <button onClick={onToday} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300 hover:text-white" data-testid="agenda-today">{t("today")}</button>
        <button onClick={() => onShift(1)} className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-slate-300 hover:text-white" data-testid="agenda-next-month"><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
};

const CalendarGrid = ({ month, lang, byDay, sel, onSelect }) => {
  const cells = useMemo(() => monthCells(month), [month]);
  const weekdays = useMemo(() => Array.from({ length: 7 }, (_, i) => new Date(2024, 0, i + 1).toLocaleDateString(lang, { weekday: "short" })), [lang]);
  return (
    <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md bg-white/5">
      {weekdays.map((w) => <div key={w} className="bg-[#111827] py-2 text-center font-mono text-[10px] uppercase tracking-widest text-slate-500">{w}</div>)}
      {cells.map((d) => (
        <DayCell key={d.toISOString()} d={d} evs={byDay[dayKey(d)] || []} inMonth={d.getMonth() === month.getMonth()} isSel={sameDay(d, sel)} onSelect={onSelect} />
      ))}
    </div>
  );
};

const EventList = ({ events, empty }) => (
  <>
    {!events.length && <p className="text-sm text-slate-500">{empty}</p>}
    {events.map((e) => <EventRow key={e.kind + e.id} e={e} />)}
  </>
);

export default function Agenda() {
  const { t, lang } = useLang();
  const { data: items, reload } = useApi("/agenda", { initial: [] });
  const [month, setMonth] = useState(() => firstOfMonth(new Date()));
  const [sel, setSel] = useState(new Date());
  useLiveEvent(["notification", "community_update"], reload);

  const byDay = useMemo(() => groupByDay(items), [items]);
  const upcoming = useMemo(() => { const now = Date.now(); return items.filter((e) => isUpcoming(e, now)).slice(0, 8); }, [items]);
  const shift = (n) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));
  const goToday = () => { const d = new Date(); setSel(d); setMonth(firstOfMonth(d)); };

  return (
    <div>
      <PageHead title={t("app_agenda")} sub={t("agenda_sub")} />
      <div className="grid gap-6 lg:grid-cols-3">
        <section className="rounded-md border border-white/[0.08] bg-[#111827] p-6 lg:col-span-2" data-testid="agenda-calendar">
          <MonthNav month={month} lang={lang} onShift={shift} onToday={goToday} />
          <CalendarGrid month={month} lang={lang} byDay={byDay} sel={sel} onSelect={setSel} />
          <div className="mt-6 space-y-2" data-testid="agenda-selected-day">
            <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{sel.toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long" })}</p>
            <EventList events={byDay[dayKey(sel)] || []} empty={t("no_events_day")} />
          </div>
        </section>
        <aside className="rounded-md border border-white/[0.08] bg-[#111827] p-6" data-testid="agenda-upcoming">
          <p className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-emerald-400"><CalendarPlus className="h-4 w-4" />{t("upcoming")}</p>
          <div className="space-y-2"><EventList events={upcoming} empty={t("empty")} /></div>
        </aside>
      </div>
    </div>
  );
}
